import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';

import Loader from '@hpstellar/core/Loader';
import ProductTile from '../../product/components/product-tile';
import TopBanner from './top-banner';
import ResultsFooterBanner from './results-footer-banner';
import useProductPrice from '../../hooks/useProductPrice';
import useAddToCart from '../../hooks/useAddToCart';
import useCartState from '../../hooks/useCartState';
import useDeviceInfo from '../../hooks/useDeviceInfo';
import { ColorSelectorContainer } from '../../hooks/useColorSelector';
import useProductCompare from '../../hooks/useProductCompare';
import getProductProps from '../../product/get-product-props';
import InlineBanner from './inline-banner';
import { getPageUrl, resolveABTestData } from '../util';
import { attachDynamicData } from '../../search/util';
import ABTest from '../../abtest/index';
import useTestFlags from '../../abtest/useTestFlags';
import useProductImpressions from '../../hooks/useProductImpressionsV2';

import './vwa-product-list.less';

const DEFAULT_LAZYLOAD_SETTINGS = {
    robots: true,
    skip: true,
    offset: 800,
};

export default function VWAProductList({
    products,
    slugInfo,
    children,
    lazyLoadSettings = DEFAULT_LAZYLOAD_SETTINGS,
    firstProductRef,
    lastProductRef,
    history = {},
    loading,
    pageRefs,
    pageInView,
    setPageInView,
    infiniteScroll = false,
    filterCount,
    url,
    visId,
    orionEnabled,
    tileContainerProps,
    useProductPromos,
    loadingList,
    maxResultsPerPage = 48,
    banners,
    setDynamicBundleData,
}) {
    let allProducts = infiniteScroll ? products.reduce((acc, group) => [...acc, ...group.results], []) : products;
    const { prices } = useProductPrice(!visId ? allProducts : [], { useProductPromos });

    // Merge price data with product data when setDynamicBundleData is enabled
    if (setDynamicBundleData && prices && allProducts) {
        allProducts = attachDynamicData(allProducts, prices);
    }

    const compare = useProductCompare(visId);
    const addToCart = useAddToCart(true);
    const { cartId } = useCartState();
    const { device } = useDeviceInfo(['device']);
    const groupObserver = useRef(null);

    const pageInViewAndNotLoadingList = pageInView && !loadingList;
    useEffect(() => {
        if (!infiniteScroll) return () => {};
        if (pageInViewAndNotLoadingList) history.push(getPageUrl(url, pageInView, filterCount > 0));
    }, [pageInViewAndNotLoadingList]);

    useEffect(() => {
        if (!infiniteScroll) return () => {};
        if (!loadingList && firstProductRef?.current) {
            firstProductRef.current.scrollIntoView(true);
        }
    }, [loadingList]);

    const groupRef = useCallback(
        node => {
            if (groupObserver.current) groupObserver.current.disconnect();
            groupObserver.current = new IntersectionObserver(
                entries => {
                    let items = {};
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const value = entry.target.attributes['data-page']?.value;
                            if (!value) return;

                            if (items[value]) {
                                items[value] += 1;
                            } else {
                                items[value] = 1;
                            }
                        }
                    });

                    const key = Object.keys(items).reduce((acc, key) => {
                        if (items[key] > acc) {
                            return key;
                        }
                        return acc;
                    }, 0);

                    if (!key || key === '') return;
                    if (key !== pageInView) {
                        setPageInView(parseInt(key));
                    }
                },
                { threshold: 0.5 },
            );

            if (node?.children) {
                [...node.children].forEach(child => {
                    [...child.children].forEach(tile => {
                        groupObserver.current.observe(tile);
                    });
                });
            }
        },
        [products, pageInView],
    );

    const { analyticsData } = slugInfo || {};
    const { page_level, hsResID } = analyticsData || {};
    const hasResults = products.length > 0;
    const lazyLoadIndex = device === 'mobile' ? 1 : 5;
    const productListInfiniteScroll =
        infiniteScroll && loadingList ? [{ page: 1, results: Array(maxResultsPerPage).fill() }] : products;
    const productListWithoutInfiniteScroll =
        !infiniteScroll && loadingList ? Array(maxResultsPerPage).fill() : products;
    return (
        <div className={`vwaListContainer${visId ? ' visId' : ''}`}>
            {hasResults && <TopBanner />}
            <div className="vwaList" ref={groupRef}>
                {banners}
                {infiniteScroll &&
                    productListInfiniteScroll.map(({ page, results }) => {
                        return (
                            <div
                                id={`page${page}`}
                                className="vwaListContent"
                                data-url={getPageUrl(url, page, filterCount > 0, false)}
                                data-page={page}
                                key={page}
                            >
                                {results.map((product, i) => (
                                    <div
                                        key={`${product && product.sku}-${i}`}
                                        ref={i === 0 ? pageRefs[page] : null}
                                        className={'refWrapper'}
                                        data-page={page}
                                    >
                                        <ColorSelectorContainer product={product}>
                                            {({ colorSelection }) => {
                                                let productProps = {};
                                                if (!loadingList) {
                                                    productProps = getProductProps(
                                                        product,
                                                        prices[product.sku],
                                                        {
                                                            addToCart,
                                                            compare,
                                                            colorSelection,
                                                        },
                                                        {
                                                            resizeImage: true,
                                                            hasPriceSeoText: true,
                                                            subPromoBanner: true,
                                                            ...(orionEnabled && {
                                                                allianceBadgeProps: { placeholder: false },
                                                                combineBadges: false,
                                                            }),
                                                        },
                                                        {
                                                            list: page_level,
                                                            cartId,
                                                            position: (product && product.position) || i + 1,
                                                            hsResID,
                                                        },
                                                    );
                                                }
                                                return (
                                                    <ProductTile
                                                        {...productProps}
                                                        forceShowPlaceholder={loadingList}
                                                        lazyLoadSettings={i > lazyLoadIndex && lazyLoadSettings}
                                                        className="productTile"
                                                        ratingSpaceReserved={true}
                                                        productRef={i === results.length - 1 ? lastProductRef : null}
                                                        visId={visId}
                                                        lazyLoad={i < lazyLoadIndex ? false : true}
                                                        orionEnabled={orionEnabled}
                                                    />
                                                );
                                            }}
                                        </ColorSelectorContainer>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                {!infiniteScroll &&
                    productListWithoutInfiniteScroll.map((product, i) => {
                        const options = {
                            resizeImage: true,
                            hasPriceSeoText: true,
                        };
                        const analyticsData = {
                            list: page_level,
                            cartId,
                            position: (product && product.position) || i + 1,
                            hsResID,
                        };
                        let productProps = {};

                        if (!loadingList) {
                            productProps = visId
                                ? { product, options, analyticsData }
                                : getProductProps(
                                      product,
                                      prices[product.sku],
                                      {
                                          addToCart,
                                          compare,
                                      },
                                      {
                                          resizeImage: true,
                                          hasPriceSeoText: true,
                                          ...(orionEnabled && {
                                              allianceBadgeProps: { placeholder: false },
                                              combineBadges: false,
                                              subPromoBanner: true,
                                          }),
                                      },
                                      {
                                          list: page_level,
                                          cartId,
                                          position: (product && product.position) || i + 1,
                                          hsResID,
                                      },
                                  );
                        }
                        return (
                            <ProductTile
                                key={`${product && product.sku}-${i}`}
                                {...productProps}
                                forceShowPlaceholder={loadingList}
                                lazyLoadSettings={i > lazyLoadIndex && lazyLoadSettings}
                                className="productTile"
                                ratingSpaceReserved={true}
                                containerProps={tileContainerProps}
                                visId={visId}
                                lazyLoad={i < lazyLoadIndex ? false : true}
                                orionEnabled={orionEnabled}
                            />
                        );
                    })}
                {hasResults && <InlineBanner />}
                {children}
            </div>
            {loading && <Loader className="infLoader" />}
            {hasResults && <ResultsFooterBanner />}
        </div>
    );
}

const VWAProductImpressions = props => {
    const { page_level, trackerKey, products, disableImpressionTrigger } = props;
    // for initial page view, the products are included in the page view event
    const productImpressions = Array.isArray(products)
        ? products.reduce((allProducts, productData) => {
              const { results } = productData || {};
              if (Array.isArray(results)) {
                  allProducts = allProducts.concat(results);
              }
              return allProducts;
          }, [])
        : [];

    useProductImpressions({
        componentKey: 'finderResults',
        list: page_level,
        products: productImpressions,
        trackerKey,
        impressionGroupSize: 24,
        batchKey: 'vwa',
        disableGtmIdAssignment: true,
        disableImpressionTrigger,
    });

    return null;
};

export const VWAProductListSortABTest = props => {
    const { vwaSort } = useTestFlags(['vwaSort']);
    const { enabled: ssrLoader } = vwaSort || {};
    return (
        <ABTest experimentID="VWASortOrder" ssrRender={true} trackSubscribed>
            {data => {
                const { products: apiProducts, page_level, trackerKey } = props;
                const { getABTestProducts, hasEvaluatedGuxExperiment } = data;
                const { products, loadingList = props.loadingList } = resolveABTestData(
                    getABTestProducts,
                    apiProducts,
                ) || {
                    products: apiProducts,
                };
                //when server side test is enabled, show loader until client side test is resolved
                const isLoadingList = typeof loadingList === 'undefined' ? ssrLoader : loadingList;
                const isLoadingSortedList = typeof getABTestProducts === 'function' && loadingList;
                return (
                    <>
                        <VWAProductImpressions
                            page_level={page_level}
                            trackerKey={trackerKey}
                            products={products}
                            disableImpressionTrigger={isLoadingSortedList || !hasEvaluatedGuxExperiment}
                        />
                        <VWAProductList {...props} products={products} loadingList={isLoadingList} />
                    </>
                );
            }}
        </ABTest>
    );
};
